/**
 * FeedbackWidget - Beta Feedback floating button and modal
 * Allows users to submit feedback with rating
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Star, Send, X, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

import { API_URL } from '@/lib/api';

export default function FeedbackWidget() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const handleSubmit = async () => {
    if (!feedback.trim() && rating === 0) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/feedback`, {
        message: feedback,
        rating: rating,
        page: window.location.pathname
      }, {
        
      });

      // Show thank you animation
      setShowThankYou(true);
      
      // Trigger mascot happy state if exists
      const mascotEvent = new CustomEvent('mascot-happy');
      window.dispatchEvent(mascotEvent);

      // Reset and close after animation
      setTimeout(() => {
        setShowThankYou(false);
        setIsOpen(false);
        setFeedback('');
        setRating(0);
      }, 2500);

    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't show if not logged in
  if (!user) return null;

  return (
    <>
      {/* Floating Button - Right Side Vertical */}
      <motion.button
        initial={{ x: 100 }}
        animate={{ x: 0 }}
        transition={{ delay: 2, type: 'spring', stiffness: 200 }}
        onClick={() => setIsOpen(true)}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-40 bg-gradient-to-b from-blue-500 to-indigo-600 text-white px-2 py-4 rounded-l-lg shadow-lg hover:shadow-xl transition-shadow cursor-pointer group"
        style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
        data-testid="feedback-button"
      >
        <span className="flex items-center gap-2 text-sm font-medium">
          <MessageSquare className="w-4 h-4 rotate-90" />
          Feedback
        </span>
      </motion.button>

      {/* Feedback Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <AnimatePresence mode="wait">
            {showThankYou ? (
              // Thank You Animation
              <motion.div
                key="thankyou"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="py-12 text-center"
              >
                <motion.div
                  animate={{ 
                    scale: [1, 1.2, 1],
                    rotate: [0, 10, -10, 0]
                  }}
                  transition={{ duration: 0.5, repeat: 2 }}
                  className="text-6xl mb-4"
                >
                  ❤️
                </motion.div>
                <motion.h3
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-2xl font-bold text-gray-900 mb-2"
                >
                  Grazie!
                </motion.h3>
                <motion.p
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="text-gray-600"
                >
                  Il tuo feedback ci aiuta a migliorare Sabrina
                </motion.p>
                {/* Floating hearts animation */}
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ 
                      opacity: 0, 
                      y: 0, 
                      x: Math.random() * 200 - 100 
                    }}
                    animate={{ 
                      opacity: [0, 1, 0], 
                      y: -150,
                      x: Math.random() * 200 - 100
                    }}
                    transition={{ 
                      duration: 2, 
                      delay: i * 0.2,
                      ease: 'easeOut'
                    }}
                    className="absolute text-2xl pointer-events-none"
                    style={{ left: '50%' }}
                  >
                    💕
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              // Feedback Form
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-xl">
                    <Heart className="w-5 h-5 text-rose-500" />
                    Aiutaci a migliorare Sabrina!
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 mt-4">
                  {/* Star Rating */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Come ti trovi con Sabrina?
                    </label>
                    <div className="flex items-center gap-1" data-testid="star-rating">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setRating(star)}
                          onMouseEnter={() => setHoveredStar(star)}
                          onMouseLeave={() => setHoveredStar(0)}
                          className="p-1 transition-transform hover:scale-110"
                          data-testid={`star-${star}`}
                        >
                          <Star
                            className={`w-8 h-8 transition-colors ${
                              star <= (hoveredStar || rating)
                                ? 'fill-amber-400 text-amber-400'
                                : 'text-gray-300'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Feedback Text */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Cosa ti piace? Cosa non funziona? Hai un'idea?
                    </label>
                    <Textarea
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      placeholder="Scrivi qui il tuo feedback..."
                      className="min-h-[120px] resize-none"
                      data-testid="feedback-textarea"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting || (!feedback.trim() && rating === 0)}
                    className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                    data-testid="submit-feedback-btn"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Invio...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        Invia Feedback
                      </span>
                    )}
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    Il tuo feedback è anonimo e ci aiuta a creare un prodotto migliore
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </>
  );
}
